import * as AF from "src/types/formTemplate";
import { getFormCompleteQuestions } from "./question";

// Direct copy of https://github.com/Avela-Education/apply-form-service/blob/main/src/resources/admin/form-templates/export.ts

export type ExportContext = Readonly<{
  questions: Readonly<{ [uuid: string]: string }>;
  options: Readonly<{ [uuid: string]: string }>;
  grades: Readonly<{ [uuid: string]: string }>;
  schools: Readonly<{ [uuid: string]: string }>;
}>;

type Grade = Readonly<{
  id: string;
  label: string;
  value: string | null | undefined;
}>;

type School = Readonly<{
  id: string;
  name: string;
  reference_id: string | null;
}>;

export function getExportContext(
  schema: AF.FormTemplate<AF.WithId>,
  grades: readonly Grade[] = [],
  schools: readonly School[] = []
): ExportContext {
  const allQuestions = getFormCompleteQuestions(schema);

  // Allows mutation for when creating the context to get the most perf,
  // and return the Readonly version of the ImportContext.
  const questionsDict: { [uuid: string]: string } = {};
  const optionsDict: { [uuid: string]: string } = {};
  const gradesDict: { [uuid: string]: string } = {};
  const schoolsDict: { [uuid: string]: string } = {};

  for (const question of allQuestions) {
    if (!question.key) {
      continue;
    }

    questionsDict[question.id] = question.key;
    if ("options" in question) {
      for (const option of question.options) {
        if (option.value) {
          optionsDict[option.id] = option.value;
        }
      }
    }
  }

  for (const grade of grades) {
    if (grade.value) {
      gradesDict[grade.id] = grade.value;
    }
  }

  for (const school of schools) {
    if (school.reference_id) {
      schoolsDict[school.id] = school.reference_id;
    }
  }

  return {
    questions: questionsDict,
    options: optionsDict,
    grades: gradesDict,
    schools: schoolsDict,
  };
}

export function exportSchema(
  schema: AF.FormTemplate<AF.WithId>,
  context: ExportContext
): AF.FormTemplate<AF.WithoutId> {
  const exportedSchema = {
    ...schema,
    sections: schema.sections.map((section) => exportSection(section, context)),
  };

  return removeId(exportedSchema);
}

/**
 * Section
 */

function exportSection(
  section: AF.Section<AF.WithId>,
  context: ExportContext
): AF.Section<AF.WithId> {
  const exportedSection = { ...section };
  if ("questions" in exportedSection && "questions" in section) {
    exportedSection.questions = section.questions.map((question) =>
      exportQuestion(question, context)
    );
  }

  return exportedSection;
}

/**
 * Question
 */

function exportQuestion(
  question: AF.Question<AF.WithId>,
  context: ExportContext
): AF.Question<AF.WithId> {
  const exportedQuestion = { ...question };
  if (question.visibility) {
    exportedQuestion.visibility = exportVisibility(
      question.visibility,
      context
    );
  }

  if (exportedQuestion.type === "Grades") {
    exportedQuestion.options = []; // Empty out grades options since this should be dynamically populated
  }

  return exportedQuestion;
}

/**
 * Visibility
 */

export function exportVisibility(
  visibility: AF.Visibility.Visibility,
  context: ExportContext
): AF.Visibility.Visibility {
  const exportedVisibility: AF.Visibility.Visibility = { ...visibility };
  if (visibility.allOf) {
    const allOf = exportRules(visibility.allOf, context);
    exportedVisibility.allOf = allOf;
  } else if (visibility.anyOf) {
    const anyOf = exportRules(visibility.anyOf, context);
    exportedVisibility.anyOf = anyOf;
  } else if (visibility.oneOf) {
    const oneOf = exportRules(visibility.oneOf, context);
    exportedVisibility.oneOf = oneOf;
  }

  return exportedVisibility;
}

function exportRules(
  rulesList: AF.Visibility.Rules[],
  context: ExportContext
): AF.Visibility.Rules[] {
  const exportedRulesList: AF.Visibility.Rules[] = [];
  for (const { rules } of rulesList) {
    const exportedRules: typeof rules = [];
    for (const rule of rules) {
      const exportedRule = exportRule(rule, context);
      exportedRules.push(exportedRule);
    }

    exportedRulesList.push({ rules: exportedRules });
  }

  return exportedRulesList;
}

function exportRule(
  rule: AF.Visibility.Rule,
  context: ExportContext
): AF.Visibility.Rule {
  if (rule.dataType === "questionKey") {
    const property = exportRuleQuestionProperty(rule.property, context);
    const value = exportRuleQuestionValue(rule.value, context);
    const exportedRule: AF.Visibility.Rule = {
      ...rule,
      property,
      value,
    };
    return exportedRule;
  } else if (rule.dataType === "school") {
    const value = exportRuleSchoolValue(rule.value, context);
    const exportedRule: AF.Visibility.Rule = {
      ...rule,
      value,
    };
    return exportedRule;
  }

  return rule;
}

function exportRuleQuestionProperty(
  property: string,
  context: ExportContext
): string {
  return context.questions?.[property] ?? property;
}

function exportRuleQuestionValue(
  value: string | string[],
  context: ExportContext
): string | string[] {
  if (Array.isArray(value)) {
    const importedValue: string[] = [];
    for (const uuid of value) {
      // Check both options and grades mappings
      importedValue.push(
        context.options?.[uuid] ?? context.grades?.[uuid] ?? uuid
      );
    }
    return importedValue;
  } else {
    // Check both options and grades mappings
    return context.options?.[value] ?? context.grades?.[value] ?? value;
  }
}

function exportRuleSchoolValue(
  value: string | string[],
  context: ExportContext
): string | string[] {
  if (Array.isArray(value)) {
    const exportedValue: string[] = [];
    for (const uuid of value) {
      exportedValue.push(context.schools?.[uuid] ?? uuid);
    }
    return exportedValue;
  } else {
    return context.schools?.[value] ?? value;
  }
}

export function removeId({
  id,
  ...formTemplate
}: AF.FormTemplate<AF.WithId>): AF.FormTemplate<AF.WithoutId> {
  const sections = formTemplate.sections.map(removeSectionId);
  return {
    ...formTemplate,
    sections,
  } as AF.FormTemplate<AF.WithoutId>;
}

export function removeSectionId({
  id,
  ...section
}: AF.Section<AF.WithId>): AF.Section<AF.WithoutId> {
  const questions =
    "questions" in section
      ? section.questions.map(removeQuestionId)
      : undefined;
  return {
    ...section,
    ...(questions ? { questions } : undefined),
  } as AF.Section<AF.WithoutId>;
}

export function removeQuestionId({
  id,
  ...question
}: AF.Question<AF.WithId>): AF.Question<AF.WithoutId> {
  const options =
    "options" in question ? question.options.map(removeOptionId) : undefined;
  return {
    ...question,
    ...(options ? { options } : undefined),
  } as AF.Question<AF.WithoutId>;
}

export function removeOptionId({
  id,
  ...option
}: AF.Option<AF.WithId>): AF.Option<AF.WithoutId> {
  return option as AF.Option<AF.WithoutId>;
}
